<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useQuasar } from 'quasar';
import FsumDialog from '@/components/Utilities/FsumDialog.vue';
import FlightSchoolRepository from '@/repositories/FlightSchoolRepository';
import { useSchoolStore } from '@/stores/school';
import type FlightSchool from '@/types/FlightSchool';
import type { PrintableCourseStudent } from '@/types/FlightSchoolStudent';

const props = defineProps<{
    student: PrintableCourseStudent,
}>();

type FlightSchoolOption = {
    label?: string,
    value: FlightSchool|null,
};

const schoolStore = useSchoolStore();
const currentCourse = schoolStore.course;

const selectedSchool = ref<FlightSchoolOption>({
    label: schoolStore.school?.name,
    value: schoolStore.school,
});

const currentSchool = computed<FlightSchoolOption>(() => ({
    label: schoolStore.school?.name,
    value: schoolStore.school,
}));

// Filter the schools to only show the schools that offer the current course
const filteredSchoolOptions = computed<FlightSchoolOption[]>(() => (
    schoolStore.schools.filter(
        (school: FlightSchool) => !school.isArchived
            && school.courses.some(
                (course) => course.certificate.uuid === currentCourse?.certificate.uuid,
            ),
    ).map((school) => ({
        label: school.name,
        value: school,
    }))
));

const updateSchool = (school: FlightSchool|null) => {
    selectedSchool.value = {
        label: school?.name,
        value: school,
    };
};

const $q = useQuasar();
const router = useRouter();
const changeStudentSchool = async (school: FlightSchool) => {
    try {
        await FlightSchoolRepository.changeStudentFlightSchool(
            props.student,
            school,
        );

        $q.notify({
            type: 'positive',
            message: 'Student school changed successfully.',
        });

        router.push({
            name: 'studentRecord',
            params: {
                schoolSlug: school.urlSlug,
                part: currentCourse?.certificate.part,
                courseAcronym: currentCourse?.certificate.acronym.toLowerCase(),
                ratingAcronym: props.student.ratingAcronym.toLowerCase(),
                cusId: props.student.id,
            },
        });

        // update the current school
        schoolStore.setSchool(school);
    } catch (error: unknown) {
        /** @todo - use better error handling */
        // eslint-disable-next-line no-console
        console.error(error);
        $q.notify({
            type: 'negative',
            message: 'Failed to change student school',
        });

        updateSchool(currentSchool.value.value);
    }
};

const saveUpdatedSchool = () => {
    const school = selectedSchool.value.value;
    if (!school) return;

    $q.dialog({
        component: FsumDialog,
        componentProps: {
            title: 'Change School',
            message: `Are you sure you want to change ${props.student.name}'s school to ${school.name}?`,
            okLabel: 'Change School',
        },
    }).onOk(() => changeStudentSchool(school))
        .onCancel(() => updateSchool(currentSchool.value.value));
};

const saveButtonDisabled = computed<boolean>(() => (
    selectedSchool.value.value === currentSchool.value.value
));

const schoolSelectDisabled = computed<boolean>(() => (
    schoolStore.schools.length === 0
    || filteredSchoolOptions.value.length <= 1
    || (schoolStore.school?.isArchived ?? false)
));

const disabledSelectTooltip = computed<string>(() => {
    if (schoolStore.schools.length === 0) {
        return 'No schools available';
    }

    if (filteredSchoolOptions.value.length <= 1) {
        return 'No other schools offer this course';
    }

    if (schoolStore.school?.isArchived) {
        return 'Current school is archived';
    }

    return '';
});
</script>
<template>
    <div class="col-sm col-12 self-center">
        <q-icon
            class="q-pr-sm q-pb-xs"
            name="sym_o_school"
        />
        <span class="label">School:</span>
    </div>
    <div class="col">
        <div class="row justify-between">
            <div>
                <q-select
                    v-model="selectedSchool"
                    :options="filteredSchoolOptions"
                    style="min-width: 200px"
                    dense
                    :readonly="schoolSelectDisabled"
                    @update:model-value="(option: FlightSchoolOption) => updateSchool(option.value)"
                >
                    <q-tooltip
                        v-if="schoolSelectDisabled"
                        anchor="top middle"
                        self="bottom middle"
                        :offset="[0, 10]"
                        class="fsum-tooltip"
                    >
                        {{ disabledSelectTooltip }}
                    </q-tooltip>
                </q-select>
            </div>
            <div class="self-center">
                <q-btn
                    label="Save"
                    :ripple="false"
                    class="anchor-button text-body2"
                    color="fsum-blue"
                    no-caps
                    dense
                    flat
                    :disable="saveButtonDisabled"
                    @click="saveUpdatedSchool"
                />
            </div>
        </div>
    </div>
</template>
<style scoped>
.self-center {
   align-self: center;
}
span.label {
    font-weight: bold;
}
</style>
